import type {
  Wizard,
  WizardImplementations,
  WizardReconcilePolicy,
  WizardStepConfig,
} from './types';

/**
 * Create a typed wizard from a pure config. The step-id union (`snapshot.step`)
 * is inferred from the declared steps — you don't pass type parameters — and
 * the config is validated at module load: duplicate step ids or a `backTarget`
 * naming an undeclared step throw here, in any unit test that imports the
 * wizard, instead of dead-ending navigation at runtime.
 *
 * ```ts
 * export const setupWizard = createWizard({
 *   id: 'chronicleSetup',
 *   steps: [
 *     { id: 'default-note', validate: 'hasDefaultNote' },
 *     { id: 'language' },
 *     { id: 'firmware', enabled: 'firmwareUpdateAvailable', skippable: true },
 *   ],
 * });
 * ```
 */
export function createWizard<const TStep extends string>(config: {
  id: string;
  steps: readonly WizardStepConfig<TStep>[];
  /** Direction to move when the current step is disabled — default 'forward'. */
  reconcile?: WizardReconcilePolicy;
}): Wizard<TStep> {
  if (config.steps.length === 0) {
    throw new Error(`[@heidi/flow] Wizard "${config.id}" declares no steps.`);
  }
  const problems: string[] = [];
  const seen = new Set<string>();
  for (const step of config.steps) {
    if (seen.has(step.id)) problems.push(`duplicate step id "${step.id}"`);
    seen.add(step.id);
  }
  for (const step of config.steps) {
    if (step.backTarget !== undefined && !seen.has(step.backTarget)) {
      problems.push(
        `step "${step.id}" backTarget targets unknown step "${step.backTarget}"`
      );
    }
  }
  if (problems.length > 0) {
    throw new Error(`[@heidi/flow] Wizard "${config.id}": ${problems.join('; ')}.`);
  }
  return {
    id: config.id,
    steps: config.steps,
    stepIds: config.steps.map((step) => step.id),
    reconcile: config.reconcile ?? 'forward',
  };
}

/** Every guard name a wizard's config references (`enabled` + `validate`). */
export function requiredWizardGuardNames<TStep extends string>(
  wizard: Wizard<TStep>
): string[] {
  const names = new Set<string>();
  for (const step of wizard.steps) {
    if (step.enabled) names.add(step.enabled);
    if (step.validate) names.add(step.validate);
  }
  return [...names];
}

/**
 * Fail fast: throw if the config references a guard that `impls` doesn't
 * provide. Runs at `start()`, so a typo'd guard name surfaces immediately in
 * any unit test — not as a silent dead end mid-flow.
 */
export function validateWizardImplementations<TStep extends string>(
  wizard: Wizard<TStep>,
  impls: WizardImplementations<TStep>
): void {
  const missing = requiredWizardGuardNames(wizard).filter(
    (name) => !impls.guards?.[name]
  );
  if (missing.length === 0) return;
  throw new Error(
    `[@heidi/flow] Wizard "${wizard.id}" is missing implementations — guards: ${missing.join(', ')}.`
  );
}
